import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import './PlayerProfile.css';

function PlayerProfile({ playerId, onSelectPlayer, onSelectTeam }) {
  const [player, setPlayer] = useState(null);
  const [teammates, setTeammates] = useState([]);
  const [wiki, setWiki] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const wikiCache = useRef(new Map());

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setWiki(null);

    (async () => {
      try {
        const [detail, network] = await Promise.all([
          api.player(playerId),
          api.network(playerId, 60).catch(() => ({ nodes: [] })),
        ]);
        if (cancelled) return;
        setPlayer(detail);
        setTeammates((network.nodes || []).filter(n => !n.is_center));
        fetchWiki(detail, controller.signal);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  const fetchWiki = async (entity, signal) => {
    const key = entity.wikidata_id || entity.name;
    if (!key) return;
    if (wikiCache.current.has(key)) { setWiki(wikiCache.current.get(key)); return; }
    try {
      let title = null;
      if (entity.wikidata_id?.startsWith('Q')) {
        const r = await fetch(
          `https://www.wikidata.org/w/api.php?action=wbgetentities&ids=${entity.wikidata_id}&props=sitelinks&sitefilter=enwiki&format=json&origin=*`,
          { signal });
        if (r.ok) title = (await r.json()).entities?.[entity.wikidata_id]?.sitelinks?.enwiki?.title;
      }
      if (!title) title = entity.name;
      const s = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`, { signal });
      if (!s.ok) return;
      const data = await s.json();
      if (data.type === 'disambiguation') return;
      wikiCache.current.set(key, data);
      setWiki(data);
    } catch (e) {
      if (e.name !== 'AbortError') console.error('wiki', e);
    }
  };

  if (loading && !player) {
    return <div className="profile"><div className="profile-loading">Loading…</div></div>;
  }
  if (error) return <div className="profile"><div className="profile-error">Error: {error}</div></div>;
  if (!player) return null;

  const photo = player.image_url || '/person-placeholder.svg';
  const focus = player.image_url ? (player.face_focus || 'center 20%') : 'center';
  const wikiTitle = wiki?.titles?.canonical || wiki?.title;

  return (
    <div className="profile">
      <div className="profile-header">
        <img src={photo} alt="" className="profile-photo" style={{ objectPosition: focus }}
          onError={(e) => { e.target.src = '/person-placeholder.svg'; }} />
        <div className="profile-id">
          <h1>{player.name}</h1>
          <div className="profile-meta">
            {player.position && <span className="profile-badge">{player.position}</span>}
            {player.nationality && <span>{player.nationality}</span>}
            {player.birth_date && <span>· Born {player.birth_date}</span>}
          </div>
          {player.wikidata_id?.startsWith('Q') && (
            <a className="profile-link" href={`https://www.wikidata.org/wiki/${player.wikidata_id}`} target="_blank" rel="noreferrer">Wikidata ↗</a>
          )}
        </div>
      </div>

      {wiki?.extract && (
        <section className="profile-section">
          <div className="profile-bio">
            {wiki.thumbnail?.source && <img src={wiki.thumbnail.source} alt="" className="profile-bio-img" loading="lazy" />}
            <div>
              <p>{wiki.extract}</p>
              {wikiTitle && (
                <a className="profile-link" href={`https://en.wikipedia.org/wiki/${encodeURIComponent(wikiTitle)}`} target="_blank" rel="noreferrer">
                  Read more on Wikipedia ↗
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="profile-section">
        <h2 className="profile-section-title">Career</h2>
        {player.career?.length ? (
          <ul className="career-list">
            {player.career.map((c, i) => (
              <li key={`${c.team_id}-${c.season}-${i}`} className="career-row"
                onClick={() => onSelectTeam({ id: c.team_id, name: c.team_name })}>
                <img src={c.logo_url || '/team-placeholder.svg'} alt="" className="career-logo"
                  onError={(e) => { e.target.src = '/team-placeholder.svg'; }} />
                <span className="career-team">{c.team_name}</span>
                <span className="career-season">{c.season}</span>
                <span className="career-role">{[c.squad_position, c.jersey_number ? `#${c.jersey_number}` : null].filter(Boolean).join(' · ')}</span>
              </li>
            ))}
          </ul>
        ) : <p className="profile-empty">No career data available</p>}
      </section>

      {teammates.length > 0 && (
        <section className="profile-section">
          <h2 className="profile-section-title">Teammates <span className="profile-count">{teammates.length}</span></h2>
          <div className="roster-grid">
            {teammates.map(t => (
              <button key={t.id} className="player-card"
                onClick={() => onSelectPlayer({ id: t.id, name: t.name })}>
                <img src={t.image_url || '/person-placeholder.svg'} alt="" className="card-photo" loading="lazy"
                  style={{ objectPosition: t.image_url ? (t.face_focus || 'center 20%') : 'center' }}
                  onError={(e) => { e.target.src = '/person-placeholder.svg'; }} />
                <div className="card-name">{t.name}</div>
                <div className="card-pos">{t.position || ''}</div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default PlayerProfile;
